import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, LoginCredentials } from '../types/user';
import * as authService from '../services/auth.service';
import { supabase } from '@/clients/supabase';
import { clearAllFilterPreferences } from '../../../shared/hooks/useLocalStorageFilters';
import { refreshCSRFToken, clearCSRFToken } from '../../../shared/utils/csrf';
import { useNotification } from '../../../shared/hooks/useNotification';
import { useNetworkStatus } from '../../../shared/hooks/useNetworkStatus';


interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  isLoading: boolean;
  loading: boolean; // Alias for compatibility
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: import('../types/user').RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token storage keys
const TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
// USER_DATA_KEY removed - role is no longer cached in localStorage

// Flag to prevent race conditions
let isFetchingProfile = false;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // FIXED: Don't initialize with token decode to prevent "new user" flash
  // Wait for real data from database to avoid showing default values
  const [user, setUser] = useState<User | null>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const notification = useNotification();
  const networkStatus = useNetworkStatus();

  /**
   * Fetch user profile from Supabase with race condition prevention
   * Note: User data (including role) is NOT cached in localStorage
   * Includes timeout and token cleanup on persistent errors
   */
  const fetchUserProfile = useCallback(async (): Promise<User | null> => {
    // Prevent multiple simultaneous fetches
    if (isFetchingProfile) {
      console.log('Profile fetch already in progress, skipping...');
      return null;
    }

    isFetchingProfile = true;
    
    try {
      // Use shorter timeout for faster initial load, especially on mobile
      const userDataPromise = authService.getCurrentUser();
      const quickTimeout = Math.min(networkStatus.recommendedTimeout || 8000, 6000); // Max 6s for mobile UX
      const timeoutPromise = new Promise<null>((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), quickTimeout)
      );
      
      const userData = await Promise.race([userDataPromise, timeoutPromise]);
      
      if (!userData) {
        // If no user data returned, check if token is still valid
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          console.warn('⚠️ Token exists but no user data - clearing invalid token');
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
      }
      
      return userData;
    } catch (error: any) {
      console.error('Failed to fetch user profile:', error);
      
      // FIXED: Only clear tokens on actual auth errors, not timeouts
      const isAuthError = 
        error?.status === 401 || 
        error?.status === 403 ||
        error?.message?.includes('JWT') ||
        error?.message?.includes('expired') ||
        error?.message?.includes('invalid');
      
      // Handle different types of errors appropriately
      if (isAuthError && !error?.message?.includes('timeout')) {
        console.warn('⚠️ Auth error detected - clearing tokens');
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      } else if (error?.message?.includes('timeout')) {
        // Show network-specific notification for timeouts
        if (!networkStatus.isOnline) {
          console.warn('⚠️ Profile fetch timeout due to offline status');
        } else if (networkStatus.isSlowConnection) {
          console.warn('⚠️ Profile fetch timeout due to slow connection');
        } else {
          console.warn('⚠️ Profile fetch timeout - server may be slow');
        }
      }
      
      return null;
    } finally {
      isFetchingProfile = false;
    }
  }, []);

  /**
   * Initialize auth state on mount
   * Check for stored token and validate by fetching user data
   * Also listen for Supabase auth state changes
   * Includes timeout to prevent infinite loading
   */
  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = async () => {
      try {
        // Skip initialization if we're in password recovery flow
        const isAuthConfirmPage = window.location.pathname === '/auth/confirm';
        const isResetPasswordPage = window.location.pathname === '/reset-password';
        const isConfirmResetPage = window.location.pathname === '/confirm-reset';
        const initUrlParams = new URLSearchParams(window.location.search);
        const initUrlHashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasRecoveryType = initUrlParams.get('type') === 'recovery' || initUrlHashParams.get('type') === 'recovery';
        
        if (isAuthConfirmPage || isConfirmResetPage || (isResetPasswordPage && hasRecoveryType)) {
          console.log('ℹ️ Password recovery flow detected, skipping auth initialization');
          if (isMounted) {
            setIsLoading(false);
          }
          return;
        }
        
        // Quick check: if no token exists, skip expensive operations
        const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
        if (!token) {
          console.log('ℹ️ No token found, user not authenticated');
          if (isMounted) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }
        
        // FIXED: Always fetch fresh user data, don't use token decode for UI
        // This prevents showing default values that look like "new user"
        const userData = await fetchUserProfile();
        
        if (!isMounted) return;
        
        if (userData) {
          setUser(userData);
          console.log('✅ User profile loaded successfully');
        } else {
          // Clear tokens if fetch failed
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          sessionStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(REFRESH_TOKEN_KEY);
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear tokens on error
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Set timeout to prevent infinite loading (3 seconds max for better mobile UX)
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        console.warn('⚠️ Auth initialization timeout - setting loading to false');
        setIsLoading(false);
      }
    }, 3000);

    initializeAuth().finally(() => {
      clearTimeout(timeoutId);
    });

    // Listen for Supabase auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event);
      
      // Skip redundant INITIAL_SESSION events during reload
      if (event === 'INITIAL_SESSION') {
        if (user) {
          console.log('ℹ️ Skipping INITIAL_SESSION - user already loaded');
          return;
        }
        
        // For INITIAL_SESSION, just validate the session without fetching profile again
        if (session?.user && !isFetchingProfile) {
          console.log('✅ INITIAL_SESSION validated, keeping existing user state');
          return;
        }
      }
      
      // Handle PASSWORD_RECOVERY events - don't ignore them completely
      if (event === 'PASSWORD_RECOVERY') {
        console.log('ℹ️ PASSWORD_RECOVERY event received - user can now reset password');
        // Don't fetch profile or update user state for password recovery
        // This is just a signal that password reset is available
        return;
      }
      
      // FIXED: Prevent duplicate email confirmation notifications
      if (event === 'USER_UPDATED') {
        console.log('ℹ️ USER_UPDATED event received');
        
        // Check if this is email confirmation (user.email_confirmed_at exists)
        if (session?.user?.email_confirmed_at) {
          console.log('✅ Email confirmed at:', session.user.email_confirmed_at);
          
          // REMOVED: Email-first flow redirect logic moved to CheckEmailPage
          // CheckEmailPage now handles navigation to /email-confirmed properly
          
          // Show notification only (no redirect from AuthContext)
          const notificationShown = sessionStorage.getItem('email_confirmation_notified');
          if (!notificationShown) {
            notification.success('Email berhasil dikonfirmasi! Selamat datang di Canvango.');
            sessionStorage.setItem('email_confirmation_notified', 'true');
          }
        }
        return;
      }
      
      if (event === 'SIGNED_OUT') {
        // Auth state listener: Just ensure state is cleared
        // The logout() function already handles cleanup, so we only need to ensure consistency
        console.log('ℹ️ SIGNED_OUT event received, ensuring clean state');
        if (isMounted) {
          setUser(null);
        }
        // Clear notification flag on logout
        sessionStorage.removeItem('email_confirmation_notified');
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // FIXED: Prevent excessive auth state changes by debouncing
        if (event === 'TOKEN_REFRESHED' && user) {
          // Skip profile fetch on token refresh if user already exists
          console.log('ℹ️ Token refreshed, keeping existing user data');
          return;
        }
        
        // Update tokens and fetch user data
        if (session?.access_token) {
          localStorage.setItem(TOKEN_KEY, session.access_token);
          if (session.refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, session.refresh_token);
          }
          
          // Check URL parameters for confirmation type
          const urlSearchParams = new URLSearchParams(window.location.search);
          const urlHashParams = new URLSearchParams(window.location.hash.substring(1));
          const confirmationType = urlSearchParams.get('type') || urlHashParams.get('type');
          const isAuthConfirmPage = window.location.pathname === '/auth/confirm';
          const isResetPasswordPage = window.location.pathname === '/reset-password';
          const isConfirmResetPage = window.location.pathname === '/confirm-reset';
          
          // Handle email confirmation (type=signup) - only once
          if (confirmationType === 'signup' && event === 'SIGNED_IN') {
            console.log('✅ Email confirmation detected');
            const notificationShown = sessionStorage.getItem('email_confirmation_notified');
            if (!notificationShown) {
              notification.success('Email berhasil dikonfirmasi! Selamat datang di Canvango.');
              sessionStorage.setItem('email_confirmation_notified', 'true');
            }
            
            // Clean URL hash and search params after showing notification
            window.history.replaceState(null, '', window.location.pathname);
            
            // CRITICAL: Mark email confirmation as processed to prevent future redirects
            sessionStorage.setItem('email_confirmation_processed', 'true');
          }
          
          // For password recovery, don't fetch profile immediately to avoid race condition
          // The AuthConfirm component will handle the redirect after token exchange
          if ((confirmationType === 'recovery' || isAuthConfirmPage || isResetPasswordPage || isConfirmResetPage) && event === 'SIGNED_IN') {
            console.log('ℹ️ Password recovery flow detected, skipping profile fetch to avoid race condition');
            console.log('ℹ️ Current path:', window.location.pathname);
            console.log('ℹ️ Confirmation type:', confirmationType);
            return;
          }
          
          // FIXED: Always fetch fresh profile data to prevent default values flash
          // Remove condition that skips fetch when user exists
          if (!isFetchingProfile && isMounted) {
            const userData = await fetchUserProfile();
            if (userData && isMounted) {
              setUser(userData);
              console.log('✅ Fresh profile data loaded');
            }
          } else {
            console.log('ℹ️ Profile fetch already in progress, skipping duplicate fetch');
          }
        }
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      authListener.subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  /**
   * Auto token refresh mechanism
   * Refreshes token every 50 minutes to prevent session expiry
   * Supabase tokens expire after 1 hour by default
   */
  useEffect(() => {
    if (!user) return;

    console.log('⏰ Starting auto token refresh timer (50 minutes)');
    
    // Refresh token every 50 minutes (before 1 hour expiry)
    const refreshInterval = setInterval(async () => {
      console.log('🔄 Auto-refreshing token...');
      
      try {
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error) {
          console.error('❌ Auto token refresh failed:', error);
          
          // If refresh fails with auth error, clear session
          if (error.status === 401 || error.status === 403) {
            console.warn('⚠️ Session expired, clearing tokens...');
            notification.error('Sesi Anda telah berakhir. Silakan login kembali.');
            
            // Clear tokens directly without calling logout to avoid circular dependency
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            sessionStorage.removeItem(TOKEN_KEY);
            sessionStorage.removeItem(REFRESH_TOKEN_KEY);
            setUser(null);
          }
          return;
        }
        
        if (data.session) {
          console.log('✅ Token auto-refreshed successfully');
          
          // Update tokens in appropriate storage
          const rememberMe = localStorage.getItem('rememberMe') === 'true';
          const storage = rememberMe ? localStorage : sessionStorage;
          
          storage.setItem(TOKEN_KEY, data.session.access_token);
          if (data.session.refresh_token) {
            storage.setItem(REFRESH_TOKEN_KEY, data.session.refresh_token);
          }
        }
      } catch (error) {
        console.error('❌ Auto token refresh exception:', error);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => {
      console.log('🛑 Stopping auto token refresh timer');
      clearInterval(refreshInterval);
    };
  }, [user, notification]);

  /**
   * Role and Balance change detection mechanism
   * Uses Realtime subscription for instant updates
   * Updates user state when role or balance changes
   * FIXED: Prevent subscription restart loops
   */
  useEffect(() => {
    // Only run if user is logged in and not in email verification flow
    if (!user?.id) return;
    
    // Skip realtime subscription during email verification to prevent loops
    const isEmailVerificationFlow = window.location.pathname === '/check-email' || 
                                   window.location.pathname === '/email-confirmed';
    
    if (isEmailVerificationFlow) {
      console.log('ℹ️ Skipping Realtime subscription during email verification flow');
      return;
    }

    console.log('🔄 Starting Realtime subscription for user:', user.id);
    
    // Subscribe to user table changes for this specific user
    const channel = supabase
      .channel(`user-changes-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 User data changed:', payload);
          
          const newData = payload.new as any;
          
          // Update user state with new data using callback to avoid stale closure
          setUser((prevUser) => {
            if (!prevUser) return prevUser;
            
            // Check if role changed
            if (newData.role && newData.role !== prevUser.role) {
              console.log('🔔 Role changed:', prevUser.role, '->', newData.role);
              notification.info(`Your role has been updated to ${newData.role}`);
            }
            
            // Check if balance changed
            if (newData.balance !== undefined && newData.balance !== prevUser.balance) {
              console.log('💰 Balance changed:', prevUser.balance, '->', newData.balance);
            }
            
            return {
              ...prevUser,
              role: newData.role || prevUser.role,
              balance: newData.balance !== undefined ? newData.balance : prevUser.balance,
              username: newData.username || prevUser.username,
              email: newData.email || prevUser.email,
              fullName: newData.full_name || prevUser.fullName,
              updatedAt: newData.updated_at || prevUser.updatedAt,
            };
          });
        }
      )
      .subscribe((status) => {
        console.log('📡 Realtime subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime subscription active');
        }
      });
    
    return () => {
      console.log('🛑 Stopping Realtime subscription for user:', user.id);
      // Properly cleanup channel
      supabase.removeChannel(channel);
      console.log('✅ Realtime channel removed');
    };
  }, [user?.id, notification]); // Only depend on user.id to prevent unnecessary re-subscriptions

  /**
   * Login user with credentials using Supabase Auth
   * Note: User data is NOT cached in localStorage
   */
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      // Don't set loading state here - let the form component handle it
      // This prevents GuestRoute from re-rendering and causing issues
      
      const { token, refreshToken, user: userData } = await authService.login(credentials);

      // Determine storage based on "Ingat Saya" preference
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      const storage = rememberMe ? localStorage : sessionStorage;
      
      console.log('💾 Storing tokens in:', rememberMe ? 'localStorage (persistent)' : 'sessionStorage (temporary)');

      // Store tokens only (no user data caching)
      storage.setItem(TOKEN_KEY, token);
      if (refreshToken) {
        storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }

      // Generate new CSRF token for the session
      refreshCSRFToken();

      // Set user state (in memory only)
      setUser(userData);
      console.log('User logged in with role:', userData.role);
    } catch (error: any) {
      console.error('Login failed:', error);
      
      // Pass through the error message from auth.service (already in Indonesian)
      throw error;
    }
  };

  /**
   * Register new user with Supabase Auth
   * Note: User data is NOT cached in localStorage
   */
  const register = async (data: import('../types/user').RegisterData): Promise<void> => {
    try {
      setIsLoading(true);
      
      const userData = await authService.register(data);

      // Get session from Supabase after registration
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (sessionData?.session) {
        // Store tokens only (no user data caching)
        localStorage.setItem(TOKEN_KEY, sessionData.session.access_token);
        if (sessionData.session.refresh_token) {
          localStorage.setItem(REFRESH_TOKEN_KEY, sessionData.session.refresh_token);
        }
      }

      // Generate new CSRF token for the session
      refreshCSRFToken();

      // Set user state (in memory only)
      setUser(userData);
      console.log('User registered with role:', userData.role);
      
      // CRITICAL FIX: Wait for auth state to fully propagate
      // This ensures all auth listeners and subscriptions are properly set up
      // before any navigation occurs, preventing race conditions
      await new Promise(resolve => setTimeout(resolve, 500));
      
    } catch (error: any) {
      console.error('Registration failed:', error);
      
      // Pass through the specific error message from auth.service (already in Indonesian)
      // The auth.service already handles specific error mapping
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user and clear tokens
   * Uses timeout to prevent hanging on expired sessions
   */
  const logout = useCallback(async () => {
    console.log('🔄 Starting logout process...');
    
    // Clear user state immediately for instant UI feedback
    setUser(null);
    
    try {
      // Call Supabase logout with timeout to prevent hanging
      const logoutPromise = authService.logout();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Logout timeout')), 3000)
      );
      
      await Promise.race([logoutPromise, timeoutPromise]);
      console.log('✅ Supabase logout successful');
    } catch (error) {
      console.error('❌ Logout error (continuing with cleanup):', error);
      // Continue with cleanup even if Supabase logout fails or times out
    }
    
    // Clear tokens from storage (both localStorage and sessionStorage)
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    
    // Clear Turnstile token
    sessionStorage.removeItem('turnstile_token');
    sessionStorage.removeItem('turnstile_timestamp');
    
    // Clear CSRF token
    clearCSRFToken();
    
    // Clear filter preferences
    clearAllFilterPreferences();
    
    console.log('✅ User logged out, all data cleared');
  }, []);

  /**
   * Update user profile
   * Note: User data is NOT cached in localStorage
   */
  const updateProfile = async (data: Partial<User>): Promise<void> => {
    if (!user) {
      throw new Error('No user logged in');
    }
    
    try {
      // Update user profile in Supabase
      const { data: updatedData, error } = await supabase
        .from('users')
        .update({
          username: data.username,
          full_name: data.fullName,
          email: data.email,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      // Map to User type
      const updatedUser: User = {
        ...user,
        username: updatedData.username,
        email: updatedData.email,
        fullName: updatedData.full_name || updatedData.username,
        updatedAt: updatedData.updated_at,
      };
      
      // Update local user state (in memory only)
      setUser(updatedUser);
      console.log('Profile updated:', updatedUser);
    } catch (error: any) {
      console.error('Update profile failed:', error);
      
      if (error.code === '23505') {
        throw new Error('Username or email already exists');
      } else {
        throw new Error(error.message || 'Failed to update profile. Please try again.');
      }
    }
  };

  /**
   * Refresh user data from server
   */
  const refreshUser = async (): Promise<void> => {
    try {
      const userData = await fetchUserProfile();
      if (userData) {
        setUser(userData);
        console.log('User data refreshed:', userData);
      } else {
        console.log('Failed to refresh user data, keeping current state');
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // Keep current user state on error
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isGuest: !user,
    isLoading,
    loading: isLoading, // Alias for compatibility with Legacy components
    login,
    register,
    logout,
    updateProfile,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Get stored auth token
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get stored refresh token
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Set auth token
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Clear all auth tokens
 */
export const clearAuthTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * @deprecated User data is no longer cached in localStorage
 * Role is always fetched fresh from database
 */
export const getCachedUserData = (): User | null => {
  console.warn('getCachedUserData is deprecated - user data is no longer cached');
  return null;
};

export default AuthContext;
